import { useEffect, useState } from 'react'
import { Newspaper, ChevronDown, ChevronRight, ExternalLink, Layers } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

interface ClusterMember {
  id: number
  title: string
  source: string
  url: string | null
  published_at: string | null
}

interface Cluster {
  head: ClusterMember & { summary: string }
  members: ClusterMember[]
  size: number
  sources: string[]
  cves: string[]
  actors: string[]
  entities: string[]
}

/** News clusters — collapses N articles about the same incident into one card.
 * Reads /api/cyber/news-clusters which Jaccard-groups articles by entity overlap. */
export default function NewsClusters({ days = 3, limit = 200 }: { days?: number; limit?: number }) {
  const [data, setData] = useState<{ clusters: Cluster[]; total_articles: number; cluster_count: number; dedup_ratio: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  useEffect(() => {
    let mounted = true
    setLoading(true)
    api.get('/api/cyber/news-clusters', { params: { days, limit } })
      .then(r => { if (mounted) setData(r.data) })
      .catch(() => { if (mounted) setData(null) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [days, limit])

  if (loading) return <div className="card p-6 text-center text-text-muted text-sm">Clustering recent news…</div>
  if (!data || data.clusters.length === 0) return null

  const toggle = (i: number) => {
    const s = new Set(expanded)
    s.has(i) ? s.delete(i) : s.add(i)
    setExpanded(s)
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Layers size={14} className="text-cyan" /> News clusters — last {days}d
          <span className="text-[10px] text-text-muted font-normal">
            {data.total_articles} articles → {data.cluster_count} clusters · {data.dedup_ratio}× dedup
          </span>
        </h2>
      </div>
      <div className="space-y-2">
        {data.clusters.slice(0, 25).map((c, i) => {
          const isOpen = expanded.has(i)
          const showExpand = c.size > 1
          return (
            <div key={i} className={clsx('rounded-md border', c.size > 1 ? 'border-cyan/30 bg-cyan/[0.03]' : 'border-border')}>
              <div className="flex items-start gap-2 p-2.5">
                {showExpand ? (
                  <button type="button" onClick={() => toggle(i)} className="text-text-muted hover:text-text-primary mt-0.5 shrink-0">
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                ) : (
                  <Newspaper size={11} className="text-text-muted mt-1 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <a href={c.head.url || '#'} target="_blank" rel="noreferrer noopener"
                       className="text-text-primary text-xs font-semibold hover:text-blue-light line-clamp-2">
                      {c.head.title}
                    </a>
                    {c.head.published_at && (
                      <span className="text-[10px] text-text-muted shrink-0">
                        {formatDistanceToNow(new Date(c.head.published_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{c.head.source}</span>
                    {c.size > 1 && <span className="text-cyan font-semibold">+ {c.size - 1} more</span>}
                    {c.cves.length > 0 && (
                      <span className="flex items-center gap-1">
                        {c.cves.slice(0, 3).map(cve => (
                          <Link key={cve} to={`/app/cve/${encodeURIComponent(cve)}`}
                                onClick={e => e.stopPropagation()}
                                className="font-mono text-blue-light hover:underline">
                            {cve}
                          </Link>
                        ))}
                      </span>
                    )}
                    {c.actors.length > 0 && (
                      <span className="text-purple-300 font-mono">
                        {c.actors.slice(0, 2).join(', ')}
                      </span>
                    )}
                  </div>
                  {isOpen && (
                    <div className="mt-2 pl-2 border-l-2 border-cyan/30 space-y-1">
                      {c.members.slice(1).map(m => (
                        <a key={m.id} href={m.url || '#'} target="_blank" rel="noreferrer noopener"
                          className="block text-[10px] text-text-secondary hover:text-blue-light line-clamp-1">
                          <span className="text-text-muted">{m.source}:</span> {m.title}
                          <ExternalLink size={8} className="inline ml-1 opacity-50" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
